/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetManagedAssistantRequest,
  GetManagedAssistantSecurity,
} from "../models/operations/getmanagedassistant.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AssistantsGetManagedQueryData,
  buildAssistantsGetManagedQuery,
  prefetchAssistantsGetManaged,
  queryKeyAssistantsGetManaged,
} from "./assistantsGetManaged.core.js";
export {
  type AssistantsGetManagedQueryData,
  buildAssistantsGetManagedQuery,
  prefetchAssistantsGetManaged,
  queryKeyAssistantsGetManaged,
};

export type AssistantsGetManagedQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getManagedAssistant assistants
 *
 * @remarks
 * Get the project's built-in Project Assistant if it exists. Returns 404 when no managed assistant has been provisioned yet — call ensureManagedAssistant to create one.
 */
export function useAssistantsGetManaged(
  request?: GetManagedAssistantRequest | undefined,
  security?: GetManagedAssistantSecurity | undefined,
  options?: QueryHookOptions<
    AssistantsGetManagedQueryData,
    AssistantsGetManagedQueryError
  >,
): UseQueryResult<
  AssistantsGetManagedQueryData,
  AssistantsGetManagedQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildAssistantsGetManagedQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getManagedAssistant assistants
 *
 * @remarks
 * Get the project's built-in Project Assistant if it exists. Returns 404 when no managed assistant has been provisioned yet — call ensureManagedAssistant to create one.
 */
export function useAssistantsGetManagedSuspense(
  request?: GetManagedAssistantRequest | undefined,
  security?: GetManagedAssistantSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AssistantsGetManagedQueryData,
    AssistantsGetManagedQueryError
  >,
): UseSuspenseQueryResult<
  AssistantsGetManagedQueryData,
  AssistantsGetManagedQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAssistantsGetManagedQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAssistantsGetManagedData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: AssistantsGetManagedQueryData,
): AssistantsGetManagedQueryData | undefined {
  const key = queryKeyAssistantsGetManaged(...queryKeyBase);

  return client.setQueryData<AssistantsGetManagedQueryData>(key, data);
}

export function invalidateAssistantsGetManaged(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "assistants", "getManaged", ...queryKeyBase],
  });
}

export function invalidateAllAssistantsGetManaged(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "assistants", "getManaged"],
  });
}
